import {
  AnnualTimeframeCondition,
  annualTimeframeConditionTypes,
  FullDateTimeframeCondition,
  fullDateTimeframeConditionTypes,
} from "../marketing/segments/segment-timeframe";
import { CustomerCharacteristicType } from "../marketing/segments/segment-types";
import type {
  BooleanCompare,
  ExistsCompare,
  NumericCompare,
  WhereCondition as StoredWhereCondition,
  TokenCompare,
} from "./segment-where-condition-zod-schema";

/**
 * Conditions as the builder holds them. Dates and custom fields are flat here
 * and are serialized on the way to storage; every other branch is the stored
 * shape unchanged.
 */
export type WhereCondition<DIM extends string> =
  | Exclude<
      StoredWhereCondition<DIM>,
      {
        type:
          | WhereConditionDataType.DATE_FULL
          | WhereConditionDataType.DATE_ANNUAL
          | WhereConditionDataType.CUSTOM_FIELD;
      }
    >
  | FullDateCondition<DIM>
  | AnnualDateCondition<DIM>
  | CustomFieldCondition<DIM>;

// We might want to create dedicated currency and percentage data types
export enum WhereConditionDataType {
  TOKEN = "token",
  NUMERIC = "numeric",
  BOOLEAN = "boolean",
  DATE_FULL = "date",
  DATE_ANNUAL = "date-annual",
  TOKEN_LIST = "token_list",
  TOKEN_HIERARCHY = "token-hierarchy",
  CITY_PROXIMITY = "city-proximity",
  CUSTOM_FIELD = "custom-field",
  STRING = "string",
}

export enum ProximityUnit {
  MILES = "miles",
  KILOMETERS = "kilometers",
}

export enum ComparisonType {
  BOOLEAN = "boolean",
  EXISTS = "exists",
  LIST = "list",
  NUMERIC = "numeric",
  TOKEN = "token",
  PROXIMITY = "proximity",
  STRING = "string",
}

export enum ExistsCompareOperators {
  SET = "set",
  NOT_SET = "not_set",
}

export enum StringCompareOperators {
  CONTAINS = "contains",
  NOT_CONTAINS = "not_contains",
  STARTS_WITH = "starts_with",
  ENDS_WITH = "ends_with",
}

export interface Condition<DIM extends string, C> {
  type: WhereConditionDataType;
  dimension: DIM;
  comparison: C;
}

export enum TokenCompareOperators {
  NONE = "NONE",
  ANY = "ANY",
}

export enum ProximityCompareOperators {
  WITHIN = "WITHIN",
  OUTSIDE = "OUTSIDE",
}

export const tokenHierarchyPrerequisiteCounts: Partial<
  Record<CustomerCharacteristicType, number>
> = {
  [CustomerCharacteristicType.STATE_PROVINCE]: 1, // country
  [CustomerCharacteristicType.CITY]: 2, // country, state
  [CustomerCharacteristicType.PROXIMITY_TO_CITY]: 2, // country, state
};

export enum NumericCompareOperator {
  EQ = "eq",
  GT = "gt",
  LT = "lt",
  GTE = "gte",
  LTE = "lte",
  NEQ = "neq",
}

export interface FullDateCondition<DIM extends string> extends Condition<
  DIM,
  FullDateTimeframeCondition
> {
  type: WhereConditionDataType.DATE_FULL;
}

export interface AnnualDateCondition<DIM extends string> extends Condition<
  DIM,
  AnnualTimeframeCondition
> {
  type: WhereConditionDataType.DATE_ANNUAL;
}

export enum ListCompareOperators {
  ANY = "any",
  NONE = "none",
  ALL = "all",
}

export interface CustomFieldCondition<DIM extends string> {
  type: WhereConditionDataType.CUSTOM_FIELD;
  dimension: DIM;
  declarationId: string; // Store as string to match zod schemas
  comparison:
    | TokenCompare
    | NumericCompare
    | BooleanCompare
    | ExistsCompare
    | FullDateTimeframeCondition;
}

/** Comparison operators a dimension accepts, by its data type. */
export const whereConditionDataTypeOperators: Record<
  WhereConditionDataType,
  readonly string[]
> = {
  [WhereConditionDataType.TOKEN]: [
    TokenCompareOperators.ANY,
    TokenCompareOperators.NONE,
  ],
  [WhereConditionDataType.TOKEN_HIERARCHY]: [
    TokenCompareOperators.ANY,
    TokenCompareOperators.NONE,
  ],
  [WhereConditionDataType.TOKEN_LIST]: Object.values(ListCompareOperators),
  [WhereConditionDataType.NUMERIC]: Object.values(NumericCompareOperator),
  [WhereConditionDataType.BOOLEAN]: [],
  [WhereConditionDataType.STRING]: Object.values(StringCompareOperators),
  [WhereConditionDataType.DATE_FULL]: fullDateTimeframeConditionTypes,
  [WhereConditionDataType.DATE_ANNUAL]: annualTimeframeConditionTypes,
  [WhereConditionDataType.CITY_PROXIMITY]: Object.values(
    ProximityCompareOperators,
  ),
  [WhereConditionDataType.CUSTOM_FIELD]: [],
};
